import { useLazyQuery } from '@apollo/client'
import { isEqual } from '@deltek/specification-client/core/Utilities/objectUtils'
import { GetBulkActionStatus } from 'bpm/api/graphQL/queries/bulkActions.queries'
import ProgressDialog from 'components/ToMoveToSCS/ProgressDialog'
import debounce from 'lodash/debounce'
import React, {
    FC,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import useGlobalSnackbar from 'shared/utils/useGlobalSnackbar'

import { useListingPageStore } from '../store'

const BulkProgressStatusDialog: FC<{
    resetSelect?: () => void
}> = ({ resetSelect }) => {
    const bulkActionProgress = useListingPageStore(
        (state) => state.bulkActionProgress
    )
    const setBulkActionProgress = useListingPageStore(
        (state) => state.setBulkActionProgress
    )

    const { showGlobalSnackbar } = useGlobalSnackbar()

    const [progressStart] = useState(new Date().getTime())
    const [timeElapsed, setTimeElapsed] = useState(0)
    const [progress, setProgress] = useState(0)
    const [isProgressLoaded, setIsProgressLoaded] = useState(false)
    const [isBulkProgressOpen, setIsBulkProgressOpen] = useState(false)
    const prevBulkActionProgress = useRef({})

    const [getBulkActionProgress] = useLazyQuery(GetBulkActionStatus, {
        onCompleted: (data) => {
            if (!Object.keys(bulkActionProgress).length) return
            setProgress(
                data?.getBulkProductListingDetailStatus?.progressPercentage || 1
            )

            if (data?.getBulkProductListingDetailStatus?.errorMessage) {
                showGlobalSnackbar({
                    variables: {
                        message:
                            data?.getBulkProductListingDetailStatus
                                ?.errorMessage,
                        variant: 'error',
                        id: 'bulkActionProgressError',
                    },
                })

                const isCompletedTimeout = setTimeout(() => {
                    setBulkActionProgress({})
                    setIsBulkProgressOpen(false)
                    setIsProgressLoaded(true)

                    clearTimeout(isCompletedTimeout)
                }, 1000)
            }

            if (
                data?.getBulkProductListingDetailStatus?.progressPercentage ===
                100
            ) {
                const isCompletedTimeout = setTimeout(() => {
                    setBulkActionProgress({})
                    setIsBulkProgressOpen(false)
                    setIsProgressLoaded(true)
                    clearTimeout(isCompletedTimeout)
                }, 1000)
            }

            if (data?.getBulkProductListingDetailStatus?.result) {
                if (
                    !!data?.getBulkProductListingDetailStatus?.result
                        ?.failedBulkProductDetailTransaction &&
                    data?.getBulkProductListingDetailStatus?.result
                        ?.failedBulkProductDetailTransaction.length
                ) {
                    showGlobalSnackbar({
                        variables: {
                            message: `Failed to ${
                                Object.keys(bulkActionProgress)[0]
                            } Product/s \n\n Errors: \n${data?.getBulkProductListingDetailStatus?.result?.failedBulkProductDetailTransaction
                                ?.slice(0, 1)
                                .map(
                                    (product: Record<string, string>) =>
                                        product.productName +
                                        ' - ' +
                                        product.failureReason
                                )}`,
                            variant: 'error',
                            id: 'bulkActionProgressError',
                        },
                    })

                    const isCompletedTimeout = setTimeout(() => {
                        setBulkActionProgress({})
                        setIsBulkProgressOpen(false)
                        setIsProgressLoaded(true)

                        clearTimeout(isCompletedTimeout)
                    }, 1000)
                } else {
                    const actionMap = {
                        copy: 'Copied',
                        move: 'Moved',
                        delete: 'Deleted',
                    }

                    showGlobalSnackbar({
                        variables: {
                            message: `Successfully ${
                                actionMap[
                                    Object.keys(
                                        bulkActionProgress
                                    )[0] as keyof typeof actionMap
                                ]
                            } the Product/s`,
                            variant: 'success',
                            id: 'bulkActionProgressSuccess',
                        },
                    })

                    const isCompletedTimeout = setTimeout(() => {
                        setBulkActionProgress({})
                        setIsBulkProgressOpen(false)
                        setIsProgressLoaded(true)
                        resetSelect?.()
                        clearTimeout(isCompletedTimeout)
                    }, 1000)
                }
            }
        },
        onError: (error) => {
            showGlobalSnackbar({
                variables: {
                    message: error.message,
                    variant: 'error',
                    id: 'bulkActionProgressError',
                },
            })

            const isErroredTimeout = setTimeout(() => {
                setBulkActionProgress({})
                setIsBulkProgressOpen(false)
                setIsProgressLoaded(true)
                clearTimeout(isErroredTimeout)
            }, 1000)
        },
    })

    const fetchBulkActionProgress = useCallback(
        debounce(
            () => {
                setTimeElapsed(new Date().getTime() - progressStart)
                getBulkActionProgress({
                    variables: {
                        input: {
                            instanceId: Object.values(bulkActionProgress)[0],
                        },
                    },
                })
            },
            1000,
            { trailing: true }
        ),
        [bulkActionProgress, progressStart]
    )

    useEffect(() => {
        if (
            Object.keys(bulkActionProgress).length &&
            progress < 100 &&
            !isProgressLoaded
        ) {
            if (!isBulkProgressOpen) {
                setIsBulkProgressOpen(true)
            }

            fetchBulkActionProgress()
        }
    }, [
        bulkActionProgress,
        progress,
        fetchBulkActionProgress,
        isProgressLoaded,
        timeElapsed,
        progressStart,
    ])

    useEffect(() => {
        if (!isEqual(bulkActionProgress, prevBulkActionProgress.current)) {
            setProgress(0)
            prevBulkActionProgress.current = bulkActionProgress
            setIsProgressLoaded(false)
        }
    }, [bulkActionProgress, prevBulkActionProgress])

    const bulkAction = useMemo(() => {
        const actionKey = Object.keys(bulkActionProgress)[0]
        const bulkProcessMap: Record<string, string> = {
            copy: 'Copying',
            move: 'Moving',
            delete: 'Deleting',
            '': '',
        }

        const action = bulkProcessMap[actionKey]

        return {
            dialogName: `${action} Products`,
            statusText: `${action} ${
                actionKey !== 'delete'
                    ? 'to the selected Product Type'
                    : 'the selected product/s'
            }.`,
        }
    }, [bulkActionProgress])

    return (
        <>
            {!!isBulkProgressOpen && (
                <ProgressDialog
                    dialogId={'bulkActionProgress'}
                    dialogName={bulkAction.dialogName}
                    open={isBulkProgressOpen}
                    statusText={bulkAction.statusText}
                    progressPercentage={progress}
                    variant="New Project"
                    disableTitle={true}
                />
            )}
        </>
    )
}

export default BulkProgressStatusDialog
